// WbDriverDetailPage — /weighbridge/drivers/:enc_id (and /new).
//
// Phase C3 Group 3. Create + edit form for a single driver, plus a
// signature upload section (only when editing). Delete lives here too.
//
// CRITICAL: the server contract has NO PIN endpoint. The original C3
// prompt's `POST /:enc_id/set-pin` flow does not exist. The real
// authentication artefact is a signature image — `POST /:enc_id/signature`
// multipart upload (field name `signature`, PNG / JPEG / WEBP, 2 MB cap).
//
// Signature display: the DTO returns a storage KEY (not a URL). We
// resolve the key to a readable URL via `GET /files/presigned?key=...`.
// If that resolve fails for any reason (e.g. the key is malformed or
// the file is missing) we render a graceful fallback so the rest of
// the page still works.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { Modal } from '../../../components/ui/Modal/Modal';
import { ConfirmModal } from '../../../components/ui/ConfirmModal/ConfirmModal';
import { apiClient } from '../../../api/client';
import {
  useCreateDriver,
  useDeleteDriver,
  useUpdateDriver,
  useUploadDriverSignature,
  useWbDriver,
  type CreateDriverInput,
} from '../hooks/useWbDrivers';
import {
  WbHaulierSearch,
  type WbHaulierSearchValue,
} from '../components/WbHaulierSearch';
import { extractErrorMessage } from '../../../utils/errorMessage';
import styles from './WbDriverDetailPage.module.css';

const ALLOWED_SIGNATURE_MIMES = [
  'image/png',
  'image/jpeg',
  'image/webp',
] as const;

const SIGNATURE_MAX_BYTES = 2 * 1024 * 1024; // 2 MB

interface FormState {
  name: string;
  licence_number: string;
  phone: string;
  default_haulier: WbHaulierSearchValue | null;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  licence_number: '',
  phone: '',
  default_haulier: null,
  is_active: true,
};

export function WbDriverDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { enc_id } = useParams<{ enc_id: string }>();
  const isNew = enc_id === 'new' || enc_id === undefined;

  const driverQuery = useWbDriver(isNew ? undefined : enc_id);
  const createMut = useCreateDriver();
  const updateMut = useUpdateDriver(isNew ? '' : (enc_id as string));
  const deleteMut = useDeleteDriver(isNew ? '' : (enc_id as string));
  const uploadSigMut = useUploadDriverSignature(isNew ? '' : (enc_id as string));

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [sigModalOpen, setSigModalOpen] = useState(false);
  const [sigFile, setSigFile] = useState<File | null>(null);
  const [sigError, setSigError] = useState<string | null>(null);

  // Hydrate the form once the GET resolves.
  useEffect(() => {
    if (isNew) return;
    const d = driverQuery.data?.driver;
    if (!d) return;
    setForm({
      name: d.name,
      licence_number: d.licence_number ?? '',
      phone: d.phone ?? '',
      default_haulier: d.default_haulier
        ? { enc_id: d.default_haulier.enc_id, name: d.default_haulier.name }
        : null,
      is_active: d.is_active,
    });
  }, [isNew, driverQuery.data]);

  const driver = driverQuery.data?.driver;
  const signatureKey = driver?.signature_image_url ?? null;

  // Resolve the storage key to a readable URL via /files/presigned. If
  // it errors (missing object, permission flap, etc) we still render the
  // page — the `<img>` just won't appear and we keep the key text as a
  // fallback breadcrumb for support.
  const presignedQuery = useQuery<{ url: string }>({
    queryKey: ['wb', 'driver', enc_id ?? '', 'signature-url', signatureKey ?? ''],
    queryFn: async () => {
      if (!signatureKey) throw new Error('no signature key');
      const res = await apiClient.get<{ key: string; url: string; expires_at: string }>(
        '/files/presigned',
        { params: { key: signatureKey } },
      );
      return { url: res.data.url };
    },
    enabled: !!signatureKey && !isNew,
    staleTime: 30_000,
    retry: false,
  });

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!form.name.trim()) errs['name'] = 'Name is required.';
    if (form.name.length > 200) errs['name'] = 'Name must be 200 characters or fewer.';
    if (form.licence_number.length > 100) {
      errs['licence_number'] = 'Licence number must be 100 characters or fewer.';
    }
    if (form.phone.length > 30) {
      errs['phone'] = 'Phone must be 30 characters or fewer.';
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function nullIfEmpty(s: string): string | null {
    const t = s.trim();
    return t === '' ? null : t;
  }

  function buildBody(): CreateDriverInput {
    return {
      name: form.name.trim(),
      licence_number: nullIfEmpty(form.licence_number),
      phone: nullIfEmpty(form.phone),
      default_haulier_id: form.default_haulier?.enc_id ?? null,
      is_active: form.is_active,
    };
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!validate()) return;
    const body = buildBody();
    if (isNew) {
      createMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/drivers'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    } else {
      updateMut.mutate(body, {
        onSuccess: () => navigate('/weighbridge/drivers'),
        onError: (err) => setError(extractErrorMessage(err)),
      });
    }
  }

  function handleDeleteConfirm() {
    deleteMut.mutate(undefined, {
      onSuccess: () => {
        setConfirmDeleteOpen(false);
        navigate('/weighbridge/drivers');
      },
      onError: (err) => {
        setConfirmDeleteOpen(false);
        setError(extractErrorMessage(err));
      },
    });
  }

  function openSigModal() {
    setSigError(null);
    setSigFile(null);
    setSigModalOpen(true);
  }

  function handleSigFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSigError(null);
    const file = e.target.files?.[0] ?? null;
    if (!file) {
      setSigFile(null);
      return;
    }
    // Cheap client-side guard — server is canonical. We still catch the
    // obvious "wrong extension / too big" cases up front so the user
    // doesn't burn a round-trip on a 50 MB JPEG.
    if (!ALLOWED_SIGNATURE_MIMES.includes(file.type as typeof ALLOWED_SIGNATURE_MIMES[number])) {
      setSigError('File must be PNG, JPEG, or WEBP.');
      setSigFile(null);
      return;
    }
    if (file.size > SIGNATURE_MAX_BYTES) {
      setSigError('File must be 2 MB or smaller.');
      setSigFile(null);
      return;
    }
    setSigFile(file);
  }

  function handleSigSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSigError(null);
    if (!sigFile) {
      setSigError('Pick a file to upload.');
      return;
    }
    uploadSigMut.mutate(sigFile, {
      onSuccess: () => {
        setSigModalOpen(false);
        setSigFile(null);
      },
      onError: (err) => setSigError(extractErrorMessage(err)),
    });
  }

  const isSaving = createMut.isPending || updateMut.isPending;
  const isLoadingExisting = !isNew && driverQuery.isLoading;

  return (
    <div className={styles.page}>
      <PageHeader
        title={isNew ? 'New Driver' : form.name || 'Edit Driver'}
        subtitle={isNew ? 'Create a new driver' : 'Edit driver details'}
        actions={
          <Button
            variant="secondary"
            icon={ArrowLeft}
            onClick={() => navigate('/weighbridge/drivers')}
          >
            Back to Drivers
          </Button>
        }
      />

      {isLoadingExisting ? (
        <Card>
          <div className={styles.loading}>
            <Spinner label="Loading driver" />
          </div>
        </Card>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <Card>
            {/* -- Identity ----------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Identity</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Name</span>
                  <input
                    type="text"
                    required
                    maxLength={200}
                    value={form.name}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, name: e.target.value }))
                    }
                  />
                  {fieldErrors['name'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['name']}
                    </span>
                  ) : null}
                </label>
              </div>
            </div>

            {/* -- Contact ------------------------------------------ */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Contact</h3>
              <div className={styles.grid}>
                <label className={styles.field}>
                  <span>Licence number</span>
                  <input
                    type="text"
                    maxLength={100}
                    value={form.licence_number}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        licence_number: e.target.value,
                      }))
                    }
                  />
                  {fieldErrors['licence_number'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['licence_number']}
                    </span>
                  ) : null}
                </label>

                <label className={styles.field}>
                  <span>Phone</span>
                  <input
                    type="tel"
                    maxLength={30}
                    value={form.phone}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, phone: e.target.value }))
                    }
                  />
                  {fieldErrors['phone'] ? (
                    <span className={styles.fieldError}>
                      {fieldErrors['phone']}
                    </span>
                  ) : null}
                </label>
              </div>
            </div>

            {/* -- Default Haulier ---------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Default Haulier</h3>
              <label className={styles.field}>
                <span>Default haulier</span>
                <WbHaulierSearch
                  value={form.default_haulier}
                  onChange={(next) =>
                    setForm((prev) => ({ ...prev, default_haulier: next }))
                  }
                />
                <span className={styles.fieldHelp}>
                  Pre-selected when this driver appears on a ticket.
                </span>
              </label>
            </div>

            {/* -- Status ------------------------------------------- */}
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Status</h3>
              <label className={styles.toggleRow}>
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      is_active: e.target.checked,
                    }))
                  }
                />
                <span>Active</span>
              </label>
            </div>
          </Card>

          {/* -- Signature section ---------------------------------- */}
          {!isNew ? (
            <Card>
              <h3 className={styles.signatureHeading}>Signature</h3>
              <div className={styles.signatureBody}>
                {signatureKey ? (
                  <div className={styles.signaturePreview}>
                    {presignedQuery.data?.url ? (
                      <img
                        className={styles.signatureImage}
                        src={presignedQuery.data.url}
                        alt={`${form.name || 'Driver'} signature`}
                      />
                    ) : presignedQuery.isLoading ? (
                      <Spinner label="Loading signature" />
                    ) : (
                      <span className={styles.signatureNone}>
                        Signature uploaded — viewing UI lands in a later phase.
                      </span>
                    )}
                    <span className={styles.signatureKey}>{signatureKey}</span>
                  </div>
                ) : (
                  <span className={styles.signatureNone}>
                    No signature on file.
                  </span>
                )}

                <div className={styles.signatureActions}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={openSigModal}
                  >
                    {signatureKey ? 'Replace signature' : 'Upload signature'}
                  </Button>
                </div>
              </div>
            </Card>
          ) : null}

          {error ? <p className={styles.error}>{error}</p> : null}

          <div className={styles.actions}>
            <div>
              {!isNew ? (
                <Button
                  type="button"
                  variant="danger"
                  icon={Trash2}
                  onClick={() => setConfirmDeleteOpen(true)}
                  disabled={isSaving}
                >
                  Delete
                </Button>
              ) : null}
            </div>
            <div className={styles.rightActions}>
              <Button
                type="button"
                variant="secondary"
                onClick={() => navigate('/weighbridge/drivers')}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" loading={isSaving}>
                {isNew ? 'Create Driver' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </form>
      )}

      <ConfirmModal
        open={confirmDeleteOpen}
        title="Delete driver?"
        message={
          <p>
            Delete <strong>{form.name || 'this driver'}</strong>? This cannot
            be undone.
          </p>
        }
        variant="danger"
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDeleteOpen(false)}
        loading={deleteMut.isPending}
      />

      <Modal
        open={sigModalOpen}
        onClose={() => setSigModalOpen(false)}
        title={signatureKey ? 'Replace signature' : 'Upload signature'}
        size="sm"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setSigModalOpen(false)}
              disabled={uploadSigMut.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSigSubmit}
              loading={uploadSigMut.isPending}
            >
              Upload
            </Button>
          </>
        }
      >
        <form onSubmit={handleSigSubmit} className={styles.modalForm}>
          <label className={styles.field}>
            <span>Signature image</span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={handleSigFileChange}
            />
            <span className={styles.fieldHelp}>
              PNG / JPEG / WEBP, max 2&nbsp;MB.
            </span>
          </label>
          {sigError ? <p className={styles.error}>{sigError}</p> : null}
        </form>
      </Modal>
    </div>
  );
}
